<template>
  <div class="date-input">
    <b-form-input
      :id="id"
      ref="input"
      :value="text"
      type="text"
      inputmode="numeric"
      autocomplete="off"
      :placeholder="placeholder || displayFormat"
      :maxlength="withTime ? 16 : 10"
      :required="required"
      :disabled="disabled"
      :size="size"
      :state="state"
      :formatter="format"
      @input="onInput"
      @blur="touched = true"
    ></b-form-input>
    <b-form-invalid-feedback :state="state">
      Expected format: {{ displayFormat }}
    </b-form-invalid-feedback>
  </div>
</template>

<script>
// Keyboard date entry, displayed and typed as DD/MM/YYYY (or DD/MM/YYYY HH:mm
// with with-time). The v-model value keeps the API format: YYYY-MM-DD, or
// YYYY-MM-DDTHH:mm with with-time, and '' when the field is empty.
const pad = (n) => String(n).padStart(2, '0')

export default {
  name: 'DateInput',
  props: {
    value: { type: String, default: '' },
    id: { type: String, default: null },
    placeholder: { type: String, default: null },
    required: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    size: { type: String, default: null },
    withTime: { type: Boolean, default: false },
  },
  data () {
    return {
      text: '',
      touched: false,
    }
  },
  computed: {
    displayFormat () {
      return this.withTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY'
    },
    isValid () {
      return this.text === '' || this.parse(this.text) !== null
    },
    state () {
      // Only flag errors once the user left the field, never mark it green
      return this.touched && !this.isValid ? false : null
    },
  },
  watch: {
    value: {
      immediate: true,
      handler (value) {
        // Keep what the user is typing when the model did not really change
        if (value && this.parse(this.text) === value) return
        this.text = this.toDisplay(value)
      },
    },
    isValid () {
      this.updateValidity()
    },
  },
  mounted () {
    this.updateValidity()
  },
  methods: {
    toDisplay (value) {
      if (!value) return ''
      const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(value)
      if (!match) return value
      const [, year, month, day, hours, minutes] = match
      const date = `${day}/${month}/${year}`
      return this.withTime ? `${date} ${hours || '00'}:${minutes || '00'}` : date
    },
    // Returns the API value for a complete and valid text, null otherwise
    parse (text) {
      const regex = this.withTime
        ? /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/
        : /^(\d{2})\/(\d{2})\/(\d{4})$/
      const match = regex.exec(text || '')
      if (!match) return null
      const [day, month, year, hours, minutes] = match.slice(1).map(Number)
      const date = new Date(year, month - 1, day)
      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        return null
      }
      const isoDate = `${year}-${pad(month)}-${pad(day)}`
      if (!this.withTime) return isoDate
      if (hours > 23 || minutes > 59) return null
      return `${isoDate}T${pad(hours)}:${pad(minutes)}`
    },
    // Inserts the separators while typing: 25122025 -> 25/12/2025
    mask (raw) {
      const digits = raw.replace(/\D/g, '').slice(0, this.withTime ? 12 : 8)
      let masked = ''
      for (let i = 0; i < digits.length; i++) {
        if (i === 2 || i === 4) masked += '/'
        if (i === 8) masked += ' '
        if (i === 10) masked += ':'
        masked += digits[i]
      }
      return masked
    },
    // b-form-input formatter, applied on every keystroke
    format (raw, event) {
      // Typing a separator manually after a single digit pads it: 5/ -> 05/
      const normalized = raw.replace(/(^|\/)(\d)(?=\/)/g, '$10$2')
      // Only mask when typing at the end, so editing inside the text keeps the caret
      const input = event && event.target
      const atEnd = !input || input.selectionStart === raw.length
      return atEnd ? this.mask(normalized) : raw
    },
    onInput (text) {
      this.text = text
      if (text === '') {
        this.$emit('input', '')
        return
      }
      const parsed = this.parse(text)
      if (parsed !== null && parsed !== this.value) this.$emit('input', parsed)
    },
    updateValidity () {
      const input = this.$refs.input && this.$refs.input.$el
      if (input && input.setCustomValidity) {
        input.setCustomValidity(this.isValid ? '' : `Expected format: ${this.displayFormat}`)
      }
    },
  },
}
</script>
